import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, DotLegalPageHeader, DotLegalTable } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import FilterBar from "@common/components/filterBar/FilterBar";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import { useTranslation } from "../../localization/useTranslation";
import ExternalAuditEmptyStateIcon from "../externalAuditsEmptyState.svg?react";
import { Box, TableCell, TableRow } from "@mui/material";
import CreateLegalEntityAuditGroupDialog from "../createLegalEntityAuditGroupDialog/CreateLegalEntityAuditGroupDialog";
import { useLegalEntityAuditGroupsHooks } from "./LegalEntityAuditGroups.hooks";
import { LegalEntityAuditGroupRowTableModel } from "./LegalEntityAuditGroups.types";
import { WhiteLockIcon } from "../../common/icons";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../useUrlProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import SavedViewControls from "@common/components/savedViews/SavedViewControls";
import SavedViewPicker from "@common/components/savedViews/SavedViewPicker";
import AuditGroupTableCell from "@common/components/questionnaireComponents/auditGroupTable/AuditGroupTableCell";
import { ExternalAuditGroupFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import PlanDialogButtonWrapper from "../../plan/planDialogWrapper/planDialogWrapper.tsx";
import { EntityType } from "../../common/Common.types.ts";
import { useAddTaskMenuItem } from "../../common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function LegalEntityAuditGroups() {
    const navigate = useNavigate();
    const addTaskMenuItem = useAddTaskMenuItem();
    const { getAuditGroupDraftUrl, getAuditGroupUrl } = useUrlProvider();
    const { vendorManagementFeatures } = usePlanContext();
    const { translateString } = useTranslation();
    const {
        setShowCreateDialog,
        showCreateDialog,
        isLoading,
        rows,
        hasData,
        getFilterDataOptions,
        searchString,
        setSearchString,
        auditGroupToEdit,
        setAuditGroupToEdit,
        refetchAuditGroups,
        pageView,
        tableFields,
        tableHeaders,
        onExportClick,
    } = useLegalEntityAuditGroupsHooks();

    function getFilters() {
        return (
            <FilterBar
                isLoading={isLoading}
                filterData={getFilterDataOptions()}
                fetchFilterData
                onMenuClose={pageView.onFilterChange}
                onRemove={pageView.onFilterChange}
                pinnedFilterIds={pageView.pinnedFilterIds}
                onPinnedFilterIdsChange={pageView.onPinnedFilterIdsChange}
                onClearFilter={pageView.hasFilterChanged ? () => pageView.clearFilterData() : undefined}
                clearFilterText={pageView.hasFilterChanged ? (pageView.isSavedView ? "savedView" : "defaultView") : undefined}
                searchValue={searchString}
                searchLabel={translateString("search")}
                onSearchChange={(value) => setSearchString(value)}
            />
        );
    }

    const getAuditGroupUrlForRow = (auditGroupId: string, sent: Date | undefined) => {
        return sent ? getAuditGroupUrl(auditGroupId) : getAuditGroupDraftUrl(auditGroupId);
    };

    const getMenuItems = (row: LegalEntityAuditGroupRowTableModel) => {
        let menuItems = [];

        if (vendorManagementFeatures.auditGroups) {
            menuItems.push({
                menuItem: translateString("overview"),
                onClick: () => {
                    navigate(getAuditGroupUrlForRow(row.id, row.sent));
                },
            });

            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setAuditGroupToEdit(row);
                },
            });

            menuItems.push({
                menuItem: translateString("addLegalEntity"),
                onClick: () => {
                    navigate(getAuditGroupDraftUrl(row.id!));
                },
            });

            addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
                fromEntityId: row.id,
                fromEntityType: EntityType.LegalEntityAuditGroup,
                toEntityType: EntityType.Task,
            });
        }

        return menuItems;
    };

    return (
        <React.Fragment>
            <DotLegalPageHeader
                breadCrumbs={[{ name: translateString("externalAudits") }]}
                rightComponent={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PlanDialogButtonWrapper enabled={!vendorManagementFeatures.auditGroups} text={translateString("upgradeVendorManagementPlan")}>
                            <DotLegalButton
                                buttonType="primary"
                                size="medium"
                                onClick={() => setShowCreateDialog(true)}
                                disabled={!vendorManagementFeatures.auditGroups}
                                endIcon={!vendorManagementFeatures.auditGroups ? WhiteLockIcon : undefined}
                                toolTip={!vendorManagementFeatures.auditGroups ? translateString("upgradeVendorManagementPlan") : undefined}
                            >
                                {translateString("createAuditGroup")}
                            </DotLegalButton>
                        </PlanDialogButtonWrapper>
                    </Box>
                }
            />

            <TablePagePaper>
                {hasData ? (
                    <>
                        <ListPageToolbar
                            savedViewsMenu={<SavedViewPicker pageView={pageView} isLoading={isLoading} />}
                            leftContent={getFilters()}
                            rightContent={
                                <SavedViewControls
                                    pageView={pageView}
                                    tableFields={tableFields}
                                    groupingOrder={ExternalAuditGroupFieldGroupingOrder}
                                    isLoading={isLoading}
                                    onExportClick={onExportClick}
                                />
                            }
                        />
                        <DotLegalTable
                            internalScroll
                            sessionStateKey={pageView.tableSessionStateKey}
                            headers={tableHeaders}
                            rowsPerPage={30}
                            defaultOrderBy={pageView.sortOrderBy as keyof LegalEntityAuditGroupRowTableModel}
                            defaultOrder={pageView.sortOrder}
                            onChange={pageView.onTableStateChange}
                            isLoading={isLoading}
                            hasOverflowMenu={vendorManagementFeatures.auditGroups}
                            searchString={searchString}
                            paginationLabelOf={translateString("labelOf")}
                            emptyText={translateString("noData")}
                            data={rows}
                            renderRow={(row) => (
                                <TableRow key={row.id} onClick={() => navigate(getAuditGroupUrlForRow(row.id, row.sent))}>
                                    {pageView.renderedColumns.map((column) => (
                                        <AuditGroupTableCell key={column} column={column} row={row} linkTo={getAuditGroupUrlForRow(row.id, row.sent)} />
                                    ))}
                                    {vendorManagementFeatures.auditGroups && (
                                        <TableCell align={"right"}>
                                            <DotLegalOverflowMenu items={getMenuItems(row)} />
                                        </TableCell>
                                    )}
                                </TableRow>
                            )}
                        />
                    </>
                ) : (
                    <DotLegalEmptyState icon={<ExternalAuditEmptyStateIcon />} text={translateString("externalAuditsEmptystateText")} />
                )}
            </TablePagePaper>

            {auditGroupToEdit && (
                <CreateLegalEntityAuditGroupDialog
                    onDialogClose={() => setAuditGroupToEdit(undefined)}
                    auditGroup={{
                        description: auditGroupToEdit.description,
                        name: auditGroupToEdit.name,
                        deadline: auditGroupToEdit.deadline,
                        groupEntityId: auditGroupToEdit.senderId,
                        questionnaireId: auditGroupToEdit.questionnaire.id,
                        questionnaireName: auditGroupToEdit.questionnaire.name,
                        questionnaireVersion: auditGroupToEdit.auditTemplateVersion,
                        responsibleId: auditGroupToEdit.responsible.id,
                        relations: [],
                    }}
                    onAuditGroupUpdated={() => {
                        setAuditGroupToEdit(undefined);
                        refetchAuditGroups();
                    }}
                    auditGroupId={auditGroupToEdit.id}
                    auditGroupInProgress={auditGroupToEdit.sent !== undefined && auditGroupToEdit.sent !== null}
                />
            )}

            {showCreateDialog && <CreateLegalEntityAuditGroupDialog onDialogClose={() => setShowCreateDialog(false)} />}
        </React.Fragment>
    );
}

export default LegalEntityAuditGroups;
